import {
  isPlayedResult,
  otherColour,
  pointsFor,
  type Colour,
  type RoundEntry,
  type Score,
} from '../model/types.js';
import { colourState, type PreferenceStrength } from './colour.js';
import { effectiveNumbers } from './dutch/engine.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/**
 * FIDE Lim System, C.04.4.3 as applied from 1 February 2026.
 *
 * The procedure is followed literally where the text is procedural:
 * scoregroups from the top down to just above the median, then from the
 * bottom up, the median last and paired downward (2.2); floaters chosen
 * before a group is paired (2.3, 3.x); proposed pairings top half against
 * bottom half (2.4); exchanges in the order of 4.1-4.4 (the generator
 * `limExchanges` reproduces the tables of 4.2 and 4.3); colours by 5.1-5.6;
 * the last-round exception (6); round one (7); the PAB (1.1, Basic Rules 4).
 *
 * Interpretations, where the text is ambiguous or incomplete:
 *
 * 1. Numbering (settled by 4.2): "highest numbered" is pairing number #1, the
 *    top of the list; "lowest numbered" is the largest pairing number. Read
 *    so in 2.5, 3.2.4, 3.3, 3.4, 3.6, 3.7, 3.8, 4.1 and 4.4.2 ("higher
 *    pairing number" in 3.3 and 3.6 likewise means nearer the top).
 * 2. Compatibility (2.1) counts played games only (C.04.2 3.4): a pair is
 *    compatible when the players have not met (nor are forbidden to meet)
 *    and some colour allocation keeps both within "never the same colour
 *    three times running" and "never three more of one colour". A player
 *    whose history already breaks those limits may take either colour.
 * 3. The median score is half the points of a win per round played. When no
 *    player has it, the median group is empty but still exists: the
 *    floaters from above and from below meet there.
 * 4. The PAB is settled first: the eligible player ([C2]) with the lowest
 *    score and then the largest pairing number (round one: the lowest
 *    rating, 7.1), skipping anyone whose bye would leave the others with no
 *    legal pairing.
 * 5. Inside a group: (a) incoming floaters with no compatible player there
 *    float on (3.5); (b) residents with no compatible player float (2.3.1 to
 *    2.3.3); (c) incoming floaters are paired first, in the order of 3.6/3.7
 *    (downfloaters before upfloaters at or above the median, the reverse
 *    below), each with the first player in the order of 3.8 who is
 *    compatible and due the other colour, else the first compatible one; an
 *    upfloater uses the upward order of 3.8 even in the median; a floater who
 *    could only be paired at the cost of another pair in the group floats
 *    on; (d) an odd remainder sheds one floater (2.3.4); (e) the rest is
 *    scrutinised with exchanges.
 * 6. The floater of 2.3.4 is taken among the residents whose departure keeps
 *    the number of pairs in the group (3.10), ordered by: type d before type
 *    c (3.9: a compatible opponent in the receiving group, after the
 *    opponents of floaters paired before it there are set aside, 3.3/3.4);
 *    not floated in the previous round (3.10; a floater is someone paired
 *    with a player on another score, or given the PAB); the due colour that
 *    evens the numbers due White and Black (3.2.2); then the lowest numbered
 *    player downward, the highest numbered upward (3.2.4).
 * 7. Exchange order, generalising 4.2/4.3: the scrutinised player (pivot) is
 *    always the first player not yet settled. Its candidates are the bottom
 *    half of the unsettled players in order, then the top half from its
 *    bottom upwards, then the players already settled, last first. Taking a
 *    settled pivot's opponent moves that pivot on to the next compatible
 *    unsettled player in its own list (4.3, fourth arrangement); taking a
 *    settled pivot reopens its pair (4.3, fifth). The rest is always the
 *    proposal (2.4) on the unsettled players. The engine does not use the
 *    last kind of move, which never shortens the scrutiny.
 * 8. An exchange is accepted only if the players still unsettled can be
 *    paired as fully as before (a matching look-ahead), so the scrutiny
 *    never ends in an unpairable remainder. A pivot floats (4.4) only if no
 *    maximum pairing of the group includes it; the evenness floater then
 *    returns if that gains a pair (4.4.1), otherwise the lowest numbered
 *    remaining player who can be spared floats with it (4.4.2).
 * 9. The second scrutiny (5.2) is merged into the first: each pivot takes
 *    the first acceptable candidate due the other colour, without giving a
 *    displaced pivot a colour clash it did not have, else the first
 *    acceptable candidate. Colour exchanges never cost compatibility or
 *    pairs. Groups paired upward are scrutinised from the bottom (4.1.2), so
 *    the lower players' colours come first there (as 5.4 says below the
 *    median).
 * 10. Due colour: the equalising colour when the colour difference is not
 *    zero (5.5, 5.6), otherwise the alternating one (5.1). When both players
 *    are due the same colour: an allocation forced by 5.1.1/5.1.2 (5.3);
 *    the stronger need (an equalising colour beats alternation); the
 *    colour histories, latest differing round first (5.4); identical
 *    histories: the higher-ranked player at or above the median, the lower
 *    ranked below it, where "below" means the pair was made in a group below
 *    the median. Two players who have not played: the higher-ranked gets
 *    the initial colour if his pairing number among the players taking part
 *    is odd (7.2).
 * 11. Blocked median (2.6): while the median group cannot be paired, crack
 *    the pairing made last in the nearest group with pairs on the side 2.6.1
 *    or 2.6.2 names (the other side when that one has nothing left) and
 *    re-pair the median with its players as extra floaters. Because the PAB
 *    leaves a legal pairing, this always ends with one.
 * 12. Last round (6): players with the same score who have not met are
 *    compatible whatever their colours. If even so no pairing exists, the
 *    colour limits are set aside for every pair rather than failing.
 *    Outside the last round a field with no legal pairing is a PairingError.
 * 13. Maxi-tournaments (option `maxi`, default off): colour-motivated
 *    choices (exchanges 5.7, the floater's opponent 3.8, the floater's
 *    colour 3.2.3) only between players within 100 rating points of the
 *    choice made without colours.
 * 14. Boards are ordered by C.04.2 3.6.
 */

export interface LimOptions {
  /** Maxi-tournament 100-point rating limits (3.2.3, 3.8, 5.7). Default off. */
  readonly maxi?: boolean;
}

type Direction = 'down' | 'up';
type GroupKind = 'above' | 'median' | 'below';

interface LPlayer {
  /** Pairing number (TPN). */
  readonly id: number;
  /** Index into the compatibility matrix. */
  readonly idx: number;
  readonly rating: number;
  readonly score: Score;
  readonly colours: readonly Colour[];
  readonly difference: number;
  /** Due colour (equalising, else alternating), null before the first game. */
  readonly preference: Colour | null;
  readonly strength: PreferenceStrength;
  /** Colours allowed by 5.1.1 and 5.1.2 this round. */
  readonly permitted: readonly Colour[];
  readonly cannotMeet: ReadonlySet<number>;
  readonly pabEligible: boolean;
  readonly floatedLastRound: boolean;
}

interface Ctx {
  readonly players: readonly LPlayer[];
  readonly byId: ReadonlyMap<number, LPlayer>;
  readonly n: number;
  readonly compat: Uint8Array;
  readonly maxi: boolean;
}

function buildPlayers(req: PairingRequest): LPlayer[] {
  const ps = req.pointSystem;
  const byId = new Map(req.players.map((p) => [p.id, p]));
  const forbidden = new Map<number, Set<number>>();
  for (const [a, b] of req.forbiddenPairs ?? []) {
    for (const [x, y] of [
      [a, b],
      [b, a],
    ] as const) {
      let s = forbidden.get(x);
      if (s === undefined) forbidden.set(x, (s = new Set()));
      s.add(y);
    }
  }
  const pointsAfter = (p: PairingPlayer, rounds: number): Score => {
    let s = 0;
    for (let r = 0; r < rounds && r < p.history.length; r++) {
      s += pointsFor((p.history[r] as RoundEntry).result, ps);
    }
    return s;
  };
  const prev = req.round - 2;
  const out: Omit<LPlayer, 'idx'>[] = [];
  for (const p of req.players) {
    if (!p.available) continue;
    const cs = colourState(p.history);
    let pabEligible = true;
    const cannotMeet = new Set<number>(forbidden.get(p.id) ?? []);
    for (const e of p.history) {
      if (isPlayedResult(e.result) && e.opponent !== null) cannotMeet.add(e.opponent);
      else if (e.result === 'U' || pointsFor(e.result, ps) >= ps.win) pabEligible = false;
    }
    const c = cs.colours;
    const last = c[c.length - 1];
    const before = c[c.length - 2];
    const allowed = (col: Colour): boolean =>
      !(last === col && before === col) && Math.abs(cs.difference + (col === 'w' ? 1 : -1)) <= 2;
    let permitted: Colour[] = (['w', 'b'] as const).filter(allowed);
    if (permitted.length === 0) permitted = ['w', 'b'];
    let floatedLastRound = false;
    const e = prev >= 0 ? p.history[prev] : undefined;
    if (e !== undefined) {
      if (e.result === 'U') floatedLastRound = true;
      else if (e.opponent !== null) {
        const opp = byId.get(e.opponent);
        floatedLastRound = opp !== undefined && pointsAfter(opp, prev) !== pointsAfter(p, prev);
      }
    }
    out.push({
      id: p.id,
      rating: p.rating,
      score: pointsAfter(p, p.history.length) + (p.acceleration ?? 0),
      colours: c,
      difference: cs.difference,
      preference: cs.preference,
      strength: cs.strength,
      permitted,
      cannotMeet,
      pabEligible,
      floatedLastRound,
    });
  }
  return out.sort((a, b) => a.id - b.id).map((p, idx) => ({ ...p, idx }));
}

function makeCtx(
  players: readonly LPlayer[],
  lastRound: boolean,
  ignoreColours: boolean,
  maxi: boolean,
): Ctx {
  const n = players.length;
  const compat = new Uint8Array(n * n);
  for (let i = 0; i < n; i++) {
    const a = players[i] as LPlayer;
    for (let j = i + 1; j < n; j++) {
      const b = players[j] as LPlayer;
      if (a.cannotMeet.has(b.id) || b.cannotMeet.has(a.id)) continue;
      const colourOk =
        ignoreColours ||
        (lastRound && a.score === b.score) ||
        (a.permitted.includes('w') && b.permitted.includes('b')) ||
        (a.permitted.includes('b') && b.permitted.includes('w'));
      if (colourOk) {
        compat[i * n + j] = 1;
        compat[j * n + i] = 1;
      }
    }
  }
  return { players, byId: new Map(players.map((p) => [p.id, p])), n, compat, maxi };
}

const compatible = (ctx: Ctx, a: LPlayer, b: LPlayer): boolean =>
  a !== b && ctx.compat[a.idx * ctx.n + b.idx] === 1;

/** Both players are due the same colour, so one of them will not get it. */
const clash = (a: LPlayer, b: LPlayer): boolean =>
  a.preference !== null && a.preference === b.preference;

const withinMaxi = (ctx: Ctx, a: LPlayer, b: LPlayer): boolean =>
  !ctx.maxi || Math.abs(a.rating - b.rating) <= 100;

/** Maximum cardinality matching (Edmonds' blossom algorithm), vertices 0..n-1. */
function maxMatching(n: number, adj: readonly (readonly number[])[]): Int32Array {
  const match = new Int32Array(n).fill(-1);
  for (let v = 0; v < n; v++) {
    if (match[v] !== -1) continue;
    for (const to of adj[v] as number[]) {
      if (match[to] === -1) {
        match[v] = to;
        match[to] = v;
        break;
      }
    }
  }
  const parent = new Int32Array(n);
  const base = new Int32Array(n);
  const used = new Uint8Array(n);
  const blossom = new Uint8Array(n);
  const queue = new Int32Array(n);
  const at = (arr: Int32Array, i: number): number => arr[i] as number;
  const lca = (a0: number, b0: number): number => {
    const seen = new Uint8Array(n);
    let a = a0;
    for (;;) {
      a = at(base, a);
      seen[a] = 1;
      if (at(match, a) === -1) break;
      a = at(parent, at(match, a));
    }
    let b = b0;
    for (;;) {
      b = at(base, b);
      if (seen[b] === 1) return b;
      b = at(parent, at(match, b));
    }
  };
  const markPath = (v0: number, b: number, child0: number): void => {
    let v = v0;
    let child = child0;
    while (at(base, v) !== b) {
      blossom[at(base, v)] = 1;
      blossom[at(base, at(match, v))] = 1;
      parent[v] = child;
      child = at(match, v);
      v = at(parent, at(match, v));
    }
  };
  const findPath = (root: number): number => {
    used.fill(0);
    parent.fill(-1);
    for (let i = 0; i < n; i++) base[i] = i;
    used[root] = 1;
    let head = 0;
    let tail = 0;
    queue[tail++] = root;
    while (head < tail) {
      const v = at(queue, head++);
      for (const to of adj[v] as number[]) {
        if (at(base, v) === at(base, to) || at(match, v) === to) continue;
        if (to === root || (at(match, to) !== -1 && at(parent, at(match, to)) !== -1)) {
          const cur = lca(v, to);
          blossom.fill(0);
          markPath(v, cur, to);
          markPath(to, cur, v);
          for (let i = 0; i < n; i++) {
            if (blossom[at(base, i)] === 1) {
              base[i] = cur;
              if (used[i] === 0) {
                used[i] = 1;
                queue[tail++] = i;
              }
            }
          }
        } else if (at(parent, to) === -1) {
          parent[to] = v;
          if (at(match, to) === -1) return to;
          used[at(match, to)] = 1;
          queue[tail++] = at(match, to);
        }
      }
    }
    return -1;
  };
  for (let i = 0; i < n; i++) {
    if (match[i] !== -1) continue;
    let v = findPath(i);
    while (v !== -1) {
      const pv = at(parent, v);
      const ppv = at(match, pv);
      match[v] = pv;
      match[pv] = v;
      v = ppv;
    }
  }
  return match;
}

/** The largest number of compatible pairs among these players. */
function pairsPossible(ctx: Ctx, group: readonly LPlayer[]): number {
  const m = group.length;
  if (m < 2) return 0;
  const adj: number[][] = Array.from({ length: m }, () => []);
  for (let i = 0; i < m; i++) {
    const a = group[i] as LPlayer;
    for (let j = i + 1; j < m; j++) {
      if (compatible(ctx, a, group[j] as LPlayer)) {
        (adj[i] as number[]).push(j);
        (adj[j] as number[]).push(i);
      }
    }
  }
  let matched = 0;
  for (const x of maxMatching(m, adj)) if (x >= 0) matched++;
  return matched / 2;
}

const pairable = (ctx: Ctx, group: readonly LPlayer[]): boolean =>
  group.length % 2 === 0 && pairsPossible(ctx, group) === group.length / 2;

// ---------------------------------------------------------------------------
// Exchanges (4.1-4.4)

/** A pair already settled by the scrutiny: the pivot, its opponent, the pivot's exchange list. */
export interface LimSettledPair {
  readonly pivot: number;
  readonly opponent: number;
  /** The pivot's candidates, in exchange order, when it was scrutinised. */
  readonly candidates: readonly number[];
}

/** One arrangement in the exchange order for the pivot (the first unsettled player). */
export interface LimExchangeStep {
  /**
   * 'open': the pivot meets an unsettled player; 'displace': it takes a
   * settled pivot's opponent, and that pivot moves on in its own list;
   * 'settled': it meets a settled pivot, whose opponent is unsettled again.
   */
  readonly kind: 'open' | 'displace' | 'settled';
  readonly opponent: number;
  /** The settled pivot whose pair the step changes. */
  readonly displaced: number | null;
  /** Settled pairs after the step, without the pivot's own pair. */
  readonly settled: readonly LimSettledPair[];
  /** Unsettled players after the step, in scrutiny order. */
  readonly rest: readonly number[];
  /** The whole arrangement: settled pairs, the pivot's pair, the proposal on the rest. */
  readonly pairs: readonly (readonly [number, number])[];
}

/**
 * The pivot's exchange candidates (4.1-4.3). `order` is the group in scrutiny
 * order (pairing number ascending when pairing downward, descending when
 * pairing upward), `unsettled` the players not yet settled in that order
 * (the pivot first, an even number of them).
 */
export function limExchangeCandidates(
  order: readonly number[],
  unsettled: readonly number[],
  settled: readonly LimSettledPair[],
): number[] {
  const pos = new Map(order.map((id, i) => [id, i]));
  const half = Math.floor(unsettled.length / 2);
  const top = unsettled.slice(0, half);
  const bottom = unsettled.slice(half);
  const done = settled
    .flatMap((s) => [s.pivot, s.opponent])
    .sort((a, b) => (pos.get(b) ?? 0) - (pos.get(a) ?? 0));
  return [...bottom, ...top.slice(1).reverse(), ...done];
}

/** The proposed pairings of 2.4: top half against bottom half, in order. */
function propose(rest: readonly number[]): [number, number][] {
  const half = Math.floor(rest.length / 2);
  const out: [number, number][] = [];
  for (let i = 0; i < half; i++) out.push([rest[i] as number, rest[i + half] as number]);
  return out;
}

/**
 * The exchanges for the pivot, in the order of 4.1-4.3. With six players
 * pairing downward, `limExchanges([1..6], [1..6], [])` gives the table of
 * 4.2; with 1-6 settled it gives the table of 4.3. `canMeet` decides which
 * player a displaced pivot moves on to.
 */
export function* limExchanges(
  order: readonly number[],
  unsettled: readonly number[],
  settled: readonly LimSettledPair[],
  canMeet: (a: number, b: number) => boolean = () => true,
): Generator<LimExchangeStep> {
  const pivot = unsettled[0];
  if (pivot === undefined) return;
  const pos = new Map(order.map((id, i) => [id, i]));
  const inOrder = (ids: number[]): number[] =>
    ids.sort((a, b) => (pos.get(a) ?? 0) - (pos.get(b) ?? 0));
  const open = new Set(unsettled);
  const arrangement = (
    s: readonly LimSettledPair[],
    opp: number,
    rest: readonly number[],
  ): [number, number][] => [
    ...s.map((x): [number, number] => [x.pivot, x.opponent]),
    [pivot, opp],
    ...propose(rest),
  ];
  for (const x of limExchangeCandidates(order, unsettled, settled)) {
    if (open.has(x)) {
      const rest = unsettled.filter((id) => id !== pivot && id !== x);
      yield {
        kind: 'open',
        opponent: x,
        displaced: null,
        settled,
        rest,
        pairs: arrangement(settled, x, rest),
      };
      continue;
    }
    const k = settled.findIndex((s) => s.opponent === x);
    if (k >= 0) {
      const s = settled[k] as LimSettledPair;
      const from = s.candidates.indexOf(s.opponent);
      const next = s.candidates
        .slice(from + 1)
        .find((c) => open.has(c) && c !== pivot && canMeet(s.pivot, c));
      if (next === undefined) continue;
      const after = settled.map((t, i) => (i === k ? { ...t, opponent: next } : t));
      const rest = unsettled.filter((id) => id !== pivot && id !== next);
      yield {
        kind: 'displace',
        opponent: x,
        displaced: s.pivot,
        settled: after,
        rest,
        pairs: arrangement(after, x, rest),
      };
      continue;
    }
    const j = settled.findIndex((s) => s.pivot === x);
    if (j >= 0) {
      const s = settled[j] as LimSettledPair;
      const after = settled.filter((_, i) => i !== j);
      const rest = inOrder([...unsettled.filter((id) => id !== pivot), s.opponent]);
      yield {
        kind: 'settled',
        opponent: x,
        displaced: s.pivot,
        settled: after,
        rest,
        pairs: arrangement(after, x, rest),
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Pairing one scoregroup

interface Group {
  readonly label: string;
  readonly kind: GroupKind;
  readonly direction: Direction;
  readonly residents: readonly LPlayer[];
  /** Floaters from above. */
  readonly down: readonly LPlayer[];
  /** Floaters from below. */
  readonly up: readonly LPlayer[];
  /** Residents of the group that receives this group's floaters. */
  readonly adjacent: readonly LPlayer[];
}

interface Floater {
  readonly player: LPlayer;
  readonly why: string;
}

interface GroupResult {
  /** Pairs in the order they were made. */
  readonly pairs: [LPlayer, LPlayer][];
  readonly floaters: LPlayer[];
  readonly line: string;
}

const byIdAsc = (a: LPlayer, b: LPlayer): number => a.id - b.id;
const byIdDesc = (a: LPlayer, b: LPlayer): number => b.id - a.id;
const ids = (xs: readonly LPlayer[]): string => xs.map((p) => p.id).join(', ') || 'none';

function forcedReason(p: LPlayer, members: readonly LPlayer[]): string {
  const others = members.filter((x) => x !== p);
  if (others.length === 0) return 'is alone in the group (2.3.4)';
  if (others.every((x) => p.cannotMeet.has(x.id) || x.cannotMeet.has(p.id))) {
    return 'has met everyone in the group (2.3.1)';
  }
  const c = p.colours;
  if (c.length >= 2 && c[c.length - 1] === c[c.length - 2]) {
    return 'had the same colour twice running and has no compatible opponent (2.3.3)';
  }
  if (Math.abs(p.difference) >= 2)
    return 'has two more of one colour and no compatible opponent (2.3.2)';
  return 'has no compatible opponent in the group (2.3)';
}

/** First candidate in order who is compatible and keeps the group's pairs; due the other colour if possible. */
function choosePartner(
  ctx: Ctx,
  f: LPlayer,
  candidates: readonly LPlayer[],
  keepsPairs: (c: LPlayer) => boolean,
): { player: LPlayer; colour: boolean } | null {
  let first: LPlayer | null = null;
  for (const c of candidates) {
    if (!compatible(ctx, f, c) || !keepsPairs(c)) continue;
    if (first === null) first = c;
    if (!clash(f, c) && withinMaxi(ctx, c, first)) return { player: c, colour: true };
  }
  return first === null ? null : { player: first, colour: false };
}

/**
 * 3.3/3.4: does `x` have a compatible opponent in the receiving group once
 * the floaters paired before it there (higher score or nearer the top when
 * floating down; lower score or nearer the bottom when floating up) have
 * taken theirs (3.8)?
 */
function adjacentOpponent(ctx: Ctx, g: Group, x: LPlayer, outgoing: readonly LPlayer[]): boolean {
  if (g.adjacent.length === 0) return false;
  const down = g.direction === 'down';
  const before = outgoing
    .filter((y) =>
      down
        ? y.score > x.score || (y.score === x.score && y.id < x.id)
        : y.score < x.score || (y.score === x.score && y.id > x.id),
    )
    .sort((a, b) => (down ? b.score - a.score || a.id - b.id : a.score - b.score || b.id - a.id));
  const order = [...g.adjacent].sort(down ? byIdAsc : byIdDesc);
  const taken = new Set<LPlayer>();
  for (const y of before) {
    const free = order.filter((h) => !taken.has(h) && compatible(ctx, y, h));
    const h = free.find((c) => !clash(y, c)) ?? free[0];
    if (h !== undefined) taken.add(h);
  }
  return order.some((h) => !taken.has(h) && compatible(ctx, x, h));
}

/** 2.3.4 with 3.2.2-3.2.4, 3.3/3.4, 3.9 and 3.10 (interpretation 6). */
function chooseParityFloater(
  ctx: Ctx,
  g: Group,
  residents: readonly LPlayer[],
  outgoing: readonly LPlayer[],
): Floater {
  const k = pairsPossible(ctx, residents);
  const dueW = residents.filter((p) => p.preference === 'w').length;
  const dueB = residents.filter((p) => p.preference === 'b').length;
  const want: Colour | null = dueW > dueB ? 'w' : dueB > dueW ? 'b' : null;
  const typeD = new Map(residents.map((p) => [p, adjacentOpponent(ctx, g, p, outgoing)]));
  const pos = (p: LPlayer): number => (g.direction === 'down' ? -p.id : p.id);
  const colourRank = (p: LPlayer): number =>
    want === null || p.preference === want ? 0 : p.preference === null ? 1 : 2;
  const base = (a: LPlayer, b: LPlayer): number =>
    Number(!typeD.get(a)) - Number(!typeD.get(b)) ||
    Number(a.floatedLastRound) - Number(b.floatedLastRound);
  const keeps = (p: LPlayer): boolean =>
    pairsPossible(
      ctx,
      residents.filter((x) => x !== p),
    ) === k;
  const positional = [...residents].sort((a, b) => base(a, b) || pos(a) - pos(b)).find(keeps);
  let pick = [...residents]
    .sort((a, b) => base(a, b) || colourRank(a) - colourRank(b) || pos(a) - pos(b))
    .find(keeps);
  if (pick === undefined || positional === undefined) {
    throw new PairingError('internal: no evenness floater (Lim 2.3.4)');
  }
  if (!withinMaxi(ctx, pick, positional)) pick = positional;
  const parts = ['2.3.4', `type ${typeD.get(pick) ? 'd' : 'c'} (3.9)`];
  if (want !== null && pick.preference === want) parts.push('evens the colours due (3.2.2)');
  else if (want === null) parts.push('as many due White as Black (3.2.4)');
  return { player: pick, why: `to make the group even [${parts.join(', ')}]` };
}

interface Scrutiny {
  readonly pairs: [LPlayer, LPlayer][];
  readonly floaters: Floater[];
  readonly parityBack: boolean;
  readonly lines: string[];
}

/** 2.4, 4.1-4.4 and the second scrutiny of 5.2 (interpretations 7-9). */
function scrutinise(
  ctx: Ctx,
  g: Group,
  players: readonly LPlayer[],
  parity: LPlayer | null,
): Scrutiny {
  const down = g.direction === 'down';
  const cmp = (a: number, b: number): number => (down ? a - b : b - a);
  const order = [...players, ...(parity === null ? [] : [parity])].map((p) => p.id).sort(cmp);
  const get = (id: number): LPlayer => ctx.byId.get(id) as LPlayer;
  const group = (xs: readonly number[]): LPlayer[] => xs.map(get);
  const canMeet = (a: number, b: number): boolean => compatible(ctx, get(a), get(b));
  let open = players.map((p) => p.id).sort(cmp);
  let settled: LimSettledPair[] = [];
  const floaters: Floater[] = [];
  const lines: string[] = [];
  let parityBack = false;
  let total = pairsPossible(ctx, group(open));
  while (open.length > 1) {
    const pivot = open[0] as number;
    const P = get(pivot);
    const candidates = limExchangeCandidates(order, open, settled);
    const need = total - settled.length - 1;
    const unpairable = new Set<number>();
    let first: LimExchangeStep | null = null;
    let chosen: LimExchangeStep | null = null;
    for (const step of limExchanges(order, open, settled, canMeet)) {
      if (step.kind === 'settled') continue;
      const X = get(step.opponent);
      if (!compatible(ctx, P, X)) continue;
      if (pairsPossible(ctx, group(step.rest)) < need) {
        unpairable.add(step.opponent);
        continue;
      }
      if (first === null) first = step;
      if (clash(P, X) || !withinMaxi(ctx, X, get(first.opponent))) continue;
      if (step.displaced !== null) {
        const was = settled.find((s) => s.pivot === step.displaced) as LimSettledPair;
        const now = step.settled.find((s) => s.pivot === step.displaced) as LimSettledPair;
        if (clash(get(now.pivot), get(now.opponent)) && !clash(get(was.pivot), get(was.opponent))) {
          continue;
        }
      }
      chosen = step;
      break;
    }
    chosen ??= first;
    if (chosen === null) {
      // 4.4: no exchange gives the pivot a compatible opponent.
      open = open.slice(1);
      floaters.push({ player: P, why: 'has no compatible opponent left in the group (4.4)' });
      const keep = total - settled.length;
      if (parity !== null && !parityBack) {
        const back = [...open, parity.id].sort(cmp);
        if (pairsPossible(ctx, group(back)) > keep) {
          open = back;
          parityBack = true;
          lines.push(`${pivot} floats instead of ${parity.id} (4.4.1)`);
          continue;
        }
      }
      const spare = [...open]
        .reverse()
        .find((id) => pairsPossible(ctx, group(open.filter((x) => x !== id))) >= keep);
      if (spare !== undefined) {
        open = open.filter((x) => x !== spare);
        floaters.push({
          player: get(spare),
          why: `floats with ${pivot} to keep the group even (4.4.2)`,
        });
      }
      continue;
    }
    const proposed = candidates[0] as number;
    if (chosen.opponent !== proposed) {
      const Y = get(proposed);
      let reason: string;
      if (P.cannotMeet.has(proposed) || Y.cannotMeet.has(pivot)) {
        reason = `${pivot}-${proposed} have already met (2.5)`;
      } else if (!compatible(ctx, P, Y)) {
        reason = `${pivot}-${proposed} would break the colour limits (2.1, 5.3)`;
      } else if (unpairable.has(proposed)) {
        reason = `${pivot}-${proposed} would leave the rest of the group unpairable (4.4)`;
      } else {
        reason = `${pivot} and ${proposed} are due the same colour (5.2)`;
      }
      let moved = '';
      if (chosen.displaced !== null) {
        const now = chosen.settled.find((s) => s.pivot === chosen.displaced) as LimSettledPair;
        moved = `, ${now.pivot} moves on to ${now.opponent} (4.3)`;
      }
      lines.push(
        `exchange: ${reason}, so ${pivot}-${chosen.opponent}${moved} (4.1.${down ? 1 : 2})`,
      );
    }
    settled = [...chosen.settled, { pivot, opponent: chosen.opponent, candidates }];
    open = [...chosen.rest];
    total = settled.length + pairsPossible(ctx, group(open));
  }
  if (open.length === 1) {
    floaters.push({ player: get(open[0] as number), why: 'is left without an opponent (4.4)' });
  }
  return {
    pairs: settled.map((s): [LPlayer, LPlayer] => [get(s.pivot), get(s.opponent)]),
    floaters,
    parityBack,
    lines,
  };
}

function pairGroup(ctx: Ctx, g: Group): GroupResult {
  const median = g.kind === 'median';
  const details: string[] = [];
  const out: Floater[] = [];
  const pairs: [LPlayer, LPlayer][] = [];
  const dfs = [...g.down].sort((a, b) => b.score - a.score || a.id - b.id);
  const ufs = [...g.up].sort((a, b) => a.score - b.score || b.id - a.id);
  const travel = new Map<LPlayer, Direction>();
  for (const p of dfs) travel.set(p, 'down');
  for (const p of ufs) travel.set(p, 'up');
  const incoming = g.kind === 'below' ? [...ufs, ...dfs] : [...dfs, ...ufs];
  let residents = [...g.residents].sort(byIdAsc);

  // 3.5: an incoming floater with no compatible player here floats on.
  const everyone = [...incoming, ...residents];
  let pending: LPlayer[] = [];
  for (const f of incoming) {
    if (!median && !everyone.some((x) => compatible(ctx, f, x))) {
      out.push({ player: f, why: 'has no compatible opponent here (3.5)' });
    } else pending.push(f);
  }
  // 2.3.1-2.3.3: residents with no compatible player in the group.
  if (!median) {
    for (;;) {
      const members = [...pending, ...residents];
      const lone = residents.find((r) => !members.some((x) => compatible(ctx, r, x)));
      if (lone === undefined) break;
      residents = residents.filter((r) => r !== lone);
      out.push({ player: lone, why: forcedReason(lone, members) });
    }
  }

  // 3.6-3.8: incoming floaters are paired first.
  let k = pairsPossible(ctx, [...pending, ...residents]);
  while (pending.length > 0) {
    const f = pending.shift() as LPlayer;
    const dir = travel.get(f) ?? g.direction;
    const sorted = (xs: readonly LPlayer[]): LPlayer[] =>
      [...xs].sort(dir === 'down' ? byIdAsc : byIdDesc);
    const rest = [...pending, ...residents];
    const partner = choosePartner(ctx, f, [...sorted(residents), ...sorted(pending)], (c) =>
      k === 0
        ? false
        : pairsPossible(
            ctx,
            rest.filter((x) => x !== c),
          ) ===
          k - 1,
    );
    if (partner === null) {
      if (median) throw new PairingError(`internal: floater ${f.id} unpaired in the median`);
      out.push({
        player: f,
        why: 'could only be paired here at the cost of another pair (3.5)',
      });
      continue;
    }
    const q = partner.player;
    pairs.push([f, q]);
    details.push(
      `floater ${f.id} meets ${q.id} (3.8${partner.colour ? '' : '; nobody compatible is due the other colour'})`,
    );
    residents = residents.filter((x) => x !== q);
    pending = pending.filter((x) => x !== q);
    k -= 1;
  }

  // 2.3.4: make the group even.
  let parity: LPlayer | null = null;
  if (residents.length % 2 === 1) {
    if (median) throw new PairingError('internal: odd median group');
    const pick = chooseParityFloater(
      ctx,
      g,
      residents,
      out.map((o) => o.player),
    );
    parity = pick.player;
    residents = residents.filter((x) => x !== parity);
    out.push(pick);
  }

  const sc = scrutinise(ctx, g, residents, parity);
  if (sc.parityBack) {
    const i = out.findIndex((o) => o.player === parity);
    if (i >= 0) out.splice(i, 1);
  }
  pairs.push(...sc.pairs);
  out.push(...sc.floaters);
  details.push(...sc.lines);
  if (median && out.length > 0) throw new PairingError('internal: floaters out of the median');

  const parts = [
    `${g.label} (paired ${g.direction === 'down' ? 'downward' : 'upward'}): residents ${ids(g.residents)}`,
  ];
  if (dfs.length > 0) parts.push(`floaters from above ${ids(dfs)}`);
  if (ufs.length > 0) parts.push(`floaters from below ${ids(ufs)}`);
  parts.push(...details);
  parts.push(`pairs ${pairs.map(([a, b]) => `${a.id}-${b.id}`).join(' ') || 'none'}`);
  if (out.length > 0) {
    const where = g.direction === 'down' ? 'down' : 'up';
    parts.push(`floating ${where}: ${out.map((o) => `${o.player.id} ${o.why}`).join(', ')}`);
  }
  return { pairs, floaters: out.map((o) => o.player), line: parts.join('; ') + '.' };
}

// ---------------------------------------------------------------------------
// Colours (5, 7)

type LimColourRule =
  'forced' | 'both' | 'single' | 'stronger' | 'history' | 'rank' | 'initial' | 'last-round';

function colourText(rule: LimColourRule, belowMedian: boolean): string {
  switch (rule) {
    case 'forced':
      return 'the only colours that keep both within 5.1.1 and 5.1.2 (Art. 5.3)';
    case 'both':
      return 'both players get their due colour (Art. 5.1)';
    case 'single':
      return 'only one player has played and gets the due colour (Art. 5.1)';
    case 'stronger':
      return 'the equalising colour goes before alternation (Art. 5.5, 5.6)';
    case 'history':
      return 'the colour histories decide, latest differing round first (Art. 5.4)';
    case 'rank':
      return belowMedian
        ? 'identical histories below the median: the lower-ranked player gets the due colour (Art. 5.4)'
        : 'identical histories: the higher-ranked player gets the due colour (Art. 5.4)';
    case 'initial':
      return 'neither has played: the initial colour by pairing number (Art. 7)';
    case 'last-round':
      return 'last round: same-score pairing goes before the colour limits (Art. 6)';
  }
}

const STRENGTH: Record<PreferenceStrength, number> = { absolute: 3, strong: 2, mild: 1, none: 0 };

function preferredColour(
  hi: LPlayer,
  lo: LPlayer,
  belowMedian: boolean,
  initial: Colour,
  number: number,
): { colour: Colour; rule: LimColourRule } {
  const hp = hi.preference;
  const lp = lo.preference;
  if (hp === null && lp === null) {
    return { colour: number % 2 === 1 ? initial : otherColour(initial), rule: 'initial' };
  }
  if (hp === null) return { colour: otherColour(lp as Colour), rule: 'single' };
  if (lp === null) return { colour: hp, rule: 'single' };
  if (hp !== lp) return { colour: hp, rule: 'both' };
  const hs = STRENGTH[hi.strength];
  const ls = STRENGTH[lo.strength];
  if (hs !== ls) return { colour: hs > ls ? hp : otherColour(hp), rule: 'stronger' };
  const hc = hi.colours;
  const lc = lo.colours;
  for (let i = 1; i <= Math.min(hc.length, lc.length); i++) {
    const a = hc[hc.length - i] as Colour;
    if (a !== lc[lc.length - i]) return { colour: otherColour(a), rule: 'history' };
  }
  return { colour: belowMedian ? otherColour(hp) : hp, rule: 'rank' };
}

/** Colour of `hi` (the higher-ranked player) and the rule that decided it. */
function allocate(
  hi: LPlayer,
  lo: LPlayer,
  belowMedian: boolean,
  initial: Colour,
  number: number,
): { colour: Colour; rule: LimColourRule } {
  const legal = (['w', 'b'] as const).filter(
    (c) => hi.permitted.includes(c) && lo.permitted.includes(otherColour(c)),
  );
  const pref = preferredColour(hi, lo, belowMedian, initial, number);
  if (legal.length === 1 && legal[0] !== pref.colour) {
    return { colour: legal[0] as Colour, rule: 'forced' };
  }
  if (legal.length === 0) return { colour: pref.colour, rule: 'last-round' };
  return pref;
}

// ---------------------------------------------------------------------------
// The engine

/** The PAB (1.1, Basic Rules 4): null when the field is even, undefined when impossible. */
function chooseBye(ctx: Ctx, round: number): LPlayer | null | undefined {
  const all = ctx.players;
  if (all.length % 2 === 0) return pairable(ctx, all) ? null : undefined;
  const order = all
    .filter((p) => p.pabEligible)
    .sort(
      round === 1
        ? (a, b) => a.rating - b.rating || b.id - a.id
        : (a, b) => a.score - b.score || b.id - a.id,
    );
  return order.find((p) =>
    pairable(
      ctx,
      all.filter((x) => x !== p),
    ),
  );
}

interface Made {
  readonly kind: GroupKind;
  readonly score: Score;
  readonly pairs: [LPlayer, LPlayer][];
}

export class LimEngine implements PairingEngine {
  readonly id = 'lim';
  readonly name = 'FIDE Lim (C.04.4.3, 2026)';
  readonly #maxi: boolean;

  constructor(options: LimOptions = {}) {
    this.#maxi = options.maxi ?? false;
  }

  pair(request: PairingRequest): PairingResult {
    const notes: string[] = [];
    const explanation: string[] = [];
    const fmt = (s: Score): string => (s / 10).toFixed(1);
    const players = buildPlayers(request);
    if (players.length === 0) return { pairs: [], byes: [], notes, explanation };
    const lastRound = request.round >= request.totalRounds;

    let ctx = makeCtx(players, lastRound, false, this.#maxi);
    let bye = chooseBye(ctx, request.round);
    if (bye === undefined && lastRound) {
      ctx = makeCtx(players, lastRound, true, this.#maxi);
      bye = chooseBye(ctx, request.round);
      if (bye !== undefined) {
        notes.push('last round: colour limits set aside so that everyone can be paired (Art. 6)');
      }
    }
    if (bye === undefined) {
      throw new PairingError(
        'no Lim pairing exists: every pairing breaks a rematch, colour (2.1) or bye ([C2]) rule',
      );
    }

    const pool = players.filter((p) => p !== bye);
    const median = ((request.round - 1) * request.pointSystem.win) / 2;
    const scores = [...new Set(pool.map((p) => p.score))].sort((a, b) => b - a);
    const above = scores.filter((s) => s > median);
    const below = scores.filter((s) => s < median).reverse();
    const at = (s: Score): LPlayer[] => pool.filter((p) => p.score === s);
    const medianResidents = at(median);

    const order: string[] = [];
    if (above.length > 0) order.push(`${above.map(fmt).join(', ')} downward`);
    if (below.length > 0) order.push(`${below.map(fmt).join(', ')} upward`);
    order.push(
      `the median ${fmt(median)} last, downward${medianResidents.length === 0 ? ' (nobody has the median score; the floaters meet there)' : ''}`,
    );
    explanation.push(`Scoregroup order (Art. 2.2): ${order.join(', then ')}.`);
    if (bye !== null) {
      explanation.push(
        request.round === 1
          ? `Pairing-allocated bye: ${bye.id} (the lowest-rated player, Art. 7).`
          : `Pairing-allocated bye: ${bye.id} (the lowest-ranked eligible player of the lowest scoregroup that leaves a legal pairing, Art. 1.1).`,
      );
    }

    const made: Made[] = [];
    let carry: LPlayer[] = [];
    above.forEach((s, i) => {
      const r = pairGroup(ctx, {
        label: `Scoregroup ${fmt(s)}`,
        kind: 'above',
        direction: 'down',
        residents: at(s),
        down: carry,
        up: [],
        adjacent: at(above[i + 1] ?? median),
      });
      made.push({ kind: 'above', score: s, pairs: r.pairs });
      explanation.push(r.line);
      carry = r.floaters;
    });
    const fromAbove = carry;
    carry = [];
    below.forEach((s, i) => {
      const r = pairGroup(ctx, {
        label: `Scoregroup ${fmt(s)}`,
        kind: 'below',
        direction: 'up',
        residents: at(s),
        down: [],
        up: carry,
        adjacent: at(below[i + 1] ?? median),
      });
      made.push({ kind: 'below', score: s, pairs: r.pairs });
      explanation.push(r.line);
      carry = r.floaters;
    });
    const fromBelow = carry;

    // The median, with 2.6 when it is blocked.
    const extraDown: LPlayer[] = [];
    const extraUp: LPlayer[] = [];
    const medianPairs: [LPlayer, LPlayer][] = [];
    for (;;) {
      const down = [...fromAbove, ...extraDown];
      const up = [...fromBelow, ...extraUp];
      const members = [...down, ...up, ...medianResidents];
      if (members.length === 0) break;
      if (pairable(ctx, members)) {
        const r = pairGroup(ctx, {
          label: `Median scoregroup ${fmt(median)}`,
          kind: 'median',
          direction: 'down',
          residents: medianResidents,
          down,
          up,
          adjacent: [],
        });
        medianPairs.push(...r.pairs);
        explanation.push(r.line);
        break;
      }
      const nearest = (kind: GroupKind): Made | undefined =>
        made.filter((m) => m.kind === kind && m.pairs.length > 0).pop();
      const lower = nearest('below');
      const upper = nearest('above');
      let target: Made | undefined;
      let article: string;
      if (down.length > up.length && lower !== undefined) {
        target = lower;
        article = '2.6.1';
      } else if (upper !== undefined) {
        target = upper;
        article = '2.6.2';
      } else {
        target = lower;
        article = '2.6.1';
      }
      if (target === undefined) throw new PairingError('internal: blocked median (Lim 2.6)');
      const cracked = target.pairs.pop() as [LPlayer, LPlayer];
      (target.kind === 'below' ? extraUp : extraDown).push(...cracked);
      explanation.push(
        `Median blocked: pairing ${cracked[0].id}-${cracked[1].id} of scoregroup ${fmt(target.score)} is cracked and its players join the median as floaters from ${target.kind === 'below' ? 'below' : 'above'} (Art. ${article}).`,
      );
    }

    // Colours (5, 7) and board order (C.04.2 3.6).
    const all: { a: LPlayer; b: LPlayer; below: boolean }[] = [
      ...made.flatMap((m) => m.pairs.map(([a, b]) => ({ a, b, below: m.kind === 'below' }))),
      ...medianPairs.map(([a, b]) => ({ a, b, below: false })),
    ];
    const rank = (x: LPlayer, y: LPlayer): number => y.score - x.score || x.id - y.id;
    const effective = effectiveNumbers(request);
    const boards = all
      .map(({ a, b, below: belowMedian }) => {
        const [hi, lo] = rank(a, b) <= 0 ? [a, b] : [b, a];
        return { hi, lo, belowMedian };
      })
      .sort(
        (x, y) =>
          y.hi.score - x.hi.score ||
          y.hi.score + y.lo.score - (x.hi.score + x.lo.score) ||
          x.hi.id - y.hi.id,
      );
    const pairs: Pair[] = boards.map(({ hi, lo, belowMedian }, i) => {
      const { colour, rule } = allocate(
        hi,
        lo,
        belowMedian,
        request.initialColour,
        effective.get(hi.id) ?? hi.id,
      );
      const pair = colour === 'w' ? { white: hi.id, black: lo.id } : { white: lo.id, black: hi.id };
      explanation.push(
        `Board ${i + 1}: ${pair.white}-${pair.black}, colours: ${colourText(rule, belowMedian)}.`,
      );
      return pair;
    });
    return { pairs, byes: bye === null ? [] : [bye.id], notes, explanation };
  }
}
